/**
 * Lista ordini di acquisto con filtri stato e anno.
 * Bottone "Nuovo ordine" naviga a NuovoOrdine.
 * Click riga naviga a OrdineDetail.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState, useUrlNumberState } from '@lib/useUrlState';
import { Button, Card } from '@floraltech/ui';
import { useFormatters } from '@lib/formatters';
import { StatoOrdineBadge } from '../components/StatoOrdineBadge';

interface Ordine {
  id: string;
  numero: string;
  anno: number;
  stato: string;
  data_ordine: string;
  fornitore_id: string;
  importo_totale: string;
  valuta: string;
  data_consegna_attesa: string | null;
  note: string | null;
  created_at: string;
}

interface Fornitore {
  id: string;
  ragione_sociale: string | null;
  nome: string | null;
  cognome: string | null;
}

export function OrdiniList() {
  const navigate = useNavigate();
  const { currency, date } = useFormatters();
  const [ordini, setOrdini] = useState<Ordine[]>([]);
  const [fornitoriMap, setFornitoriMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [anno, setAnno] = useUrlNumberState('anno', new Date().getFullYear());
  const [statoFiltro, setStatoFiltro] = useUrlState('stato', '');

  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchFornitori = async () => {
      const res = await fetch('/api/v1/soggetti?tipo=fornitore&limit=200', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const items: Fornitore[] = Array.isArray(data) ? data : data.items ?? [];
        const map: Record<string, string> = {};
        for (const f of items) {
          map[f.id] = f.ragione_sociale || [f.nome, f.cognome].filter(Boolean).join(' ') || '—';
        }
        setFornitoriMap(map);
      }
    };
    fetchFornitori();
  }, [token]);

  useEffect(() => {
    const fetchOrdini = async () => {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('anno', String(anno));
      if (statoFiltro) params.set('stato', statoFiltro);

      const res = await fetch(`/api/v1/ordini?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setOrdini(await res.json());
      }
      setLoading(false);
    };
    fetchOrdini();
  }, [anno, statoFiltro, token]);

  const stati = ['', 'bozza', 'confermato', 'inviato', 'confermato_fornitore', 'in_spedizione', 'consegnato', 'in_ricezione', 'chiuso', 'annullato'];

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-outfit font-semibold text-foreground">
          Ordini di acquisto
        </h1>
        <Button onClick={() => navigate('/ordini/nuovo')}>Nuovo ordine</Button>
      </div>

      <div className="flex gap-3">
        <select
          value={anno}
          onChange={(e) => setAnno(Number(e.target.value))}
          className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
        >
          {[2024, 2025, 2026, 2027].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <select
          value={statoFiltro}
          onChange={(e) => setStatoFiltro(e.target.value)}
          className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
        >
          {stati.map((s) => (
            <option key={s || '__all__'} value={s}>
              {s || 'Tutti gli stati'}
            </option>
          ))}
        </select>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">Caricamento…</div>
        ) : ordini.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            Nessun ordine trovato
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Numero</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Fornitore</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Data</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Stato</th>
                <th className="text-right px-4 py-2 font-medium text-muted-foreground">Importo</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Consegna</th>
              </tr>
            </thead>
            <tbody>
              {ordini.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => navigate(`/ordini/${o.id}`)}
                  className="border-b border-border hover:bg-muted/30 cursor-pointer transition-colors duration-150"
                >
                  <td className="px-4 py-2 font-medium">{o.numero}</td>
                  <td className="px-4 py-2 text-sm">{fornitoriMap[o.fornitore_id] || '—'}</td>
                  <td className="px-4 py-2">{date(o.data_ordine)}</td>
                  <td className="px-4 py-2">
                    <StatoOrdineBadge stato={o.stato} />
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {currency(o.importo_totale)}
                  </td>
                  <td className="px-4 py-2">
                    {o.data_consegna_attesa ? date(o.data_consegna_attesa) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
